import React, { useState } from 'react';
import {
  Video,
  Plus,
  Play,
  Calendar,
  Clock,
  Link,
  Edit2,
  Trash2,
  X,
  ExternalLink,
  Users,
} from 'lucide-react';
import { ScheduledClass, AdminTeacher, AdminRole } from '../types';
import { Badge, Button, Input, Tooltip } from '../../components/core';

interface LiveClassesProps {
  classes: ScheduledClass[];
  teachers: AdminTeacher[];
  currentRole: AdminRole;
  onAddClass: (cls: ScheduledClass) => void;
  onUpdateClass: (cls: ScheduledClass) => void;
  onDeleteClass: (id: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const LiveClasses: React.FC<LiveClassesProps> = ({
  classes,
  teachers,
  currentRole,
  onAddClass,
  onUpdateClass,
  onDeleteClass,
  addToast,
}) => {
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClass, setEditingClass] = useState<ScheduledClass | null>(null);

  const isAuditor = currentRole === 'Auditor';

  const [formData, setFormData] = useState({
    title: '',
    level: 'Level 1 - Prarambhik',
    batch: 'Batch A - Weekend Morning',
    teacherId: teachers[0]?.id || 'TCH-01',
    classType: 'Regular' as 'Regular' | 'General',
    dateTime: '2026-07-26 10:00 AM',
    durationMinutes: 60,
    meetingLink: 'https://zoom.us/j/98234123415',
    status: 'Scheduled' as 'LIVE NOW' | 'Scheduled' | 'Completed' | 'Cancelled',
  });

  const filteredClasses = classes.filter((c) => {
    if (statusFilter === 'ALL') return true;
    return c.status === statusFilter;
  });

  const openAddModal = () => {
    setEditingClass(null);
    setFormData({
      title: '',
      level: 'Level 1 - Prarambhik',
      batch: 'Batch A - Weekend Morning',
      teacherId: teachers[0]?.id || 'TCH-01',
      classType: 'Regular',
      dateTime: '2026-07-26 10:00 AM',
      durationMinutes: 60,
      meetingLink: 'https://zoom.us/j/98234123415',
      status: 'Scheduled',
    });
    setIsModalOpen(true);
  };

  const openEditModal = (cls: ScheduledClass) => {
    if (isAuditor) return;
    setEditingClass(cls);
    setFormData({
      title: cls.title,
      level: cls.level,
      batch: cls.batch,
      teacherId: cls.teacherId,
      classType: cls.classType,
      dateTime: cls.dateTime,
      durationMinutes: cls.durationMinutes,
      meetingLink: cls.meetingLink,
      status: cls.status,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isAuditor) return;

    const matchedTeacher = teachers.find((t) => t.id === formData.teacherId);
    const teacherName = matchedTeacher ? matchedTeacher.name : 'Assigned Instructor';

    if (editingClass) {
      onUpdateClass({
        ...editingClass,
        ...formData,
        teacherName,
      });
      addToast('success', 'Class Schedule Updated', `Updated live class details for ${formData.title}`);
    } else {
      const newCls: ScheduledClass = {
        id: `CLS-${Math.floor(500 + Math.random() * 500)}`,
        ...formData,
        teacherName,
        registeredCount: 30,
      };
      onAddClass(newCls);
      addToast('success', 'Class Scheduled', `Scheduled ${newCls.title} for ${newCls.dateTime}`);
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Live Video Classes & Broadcasts</h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Status Filter Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="admin-input h-9 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Session Statuses</option>
            <option value="LIVE NOW">LIVE NOW Only</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Completed">Completed</option>
          </select>

          {!isAuditor && (
            <Button leftIcon={Plus} onClick={openAddModal}>
              Schedule Live Class
            </Button>
          )}
        </div>
      </div>

      {/* Classes Schedule Table */}
      <div className="admin-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
            <thead className="border-b border-[var(--gv-border)] bg-[var(--gv-surface)] font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
              <tr>
                <th className="py-2.5 px-4 font-bold">Class Title & Type</th>
                <th className="py-2.5 px-4 font-bold">Target Level & Batch</th>
                <th className="py-2.5 px-4 font-bold">Assigned Instructor</th>
                <th className="py-2.5 px-4 font-bold">Date & Duration</th>
                <th className="py-2.5 px-4 font-bold">Registered</th>
                <th className="py-2.5 px-4 font-bold">Status</th>
                <th className="py-2.5 px-4 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 font-medium bg-white">
              {filteredClasses.map((cls) => {
                const isLive = cls.status === 'LIVE NOW';
                return (
                  <tr key={cls.id} className="hover:bg-[var(--gv-surface)]/80 transition-colors">
                    <td className="py-3 px-4 font-bold text-[var(--gv-ink)]">
                      <div className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-[var(--gv-brand)] shrink-0" />
                        <div>
                          <div>{cls.title}</div>
                          <div className="text-[10px] font-mono font-normal text-[var(--gv-brand)]">{cls.id} • {cls.classType} Class</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-[var(--gv-ink)] font-semibold">{cls.level}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">{cls.batch}</div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-[var(--gv-ink)]">{cls.teacherName}</td>
                    <td className="py-3 px-4 font-mono text-[var(--gv-ink-muted)] text-[11px]">
                      <div>{cls.dateTime}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">{cls.durationMinutes} minutes</div>
                    </td>
                    <td className="py-3 px-4 font-mono text-[var(--gv-ink-muted)]">
                      {cls.registeredCount} students
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        variant={isLive ? 'danger' : cls.status === 'Scheduled' ? 'warning' : 'outline'}
                        className="font-mono gap-1"
                      >
                        {isLive && <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-pulse" />}
                        {cls.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          size="sm"
                          leftIcon={Play}
                          onClick={() => {
                            addToast('info', 'Video Launcher', `Launching session for ${cls.title}`);
                            window.open(cls.meetingLink, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          {isLive ? 'Join Live' : 'Launch'}
                        </Button>

                        {!isAuditor && (
                          <>
                            <Tooltip content="Edit Class">
                              <Button
                                variant="outline"
                                size="icon"
                                leftIcon={Edit2}
                                aria-label="Edit Class"
                                onClick={() => openEditModal(cls)}
                              />
                            </Tooltip>
                            <Tooltip content="Delete Class">
                              <Button
                                variant="outline"
                                size="icon"
                                className="text-rose-600 hover:bg-rose-50"
                                leftIcon={Trash2}
                                aria-label="Delete Class"
                                onClick={() => onDeleteClass(cls.id)}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[9990] bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="admin-card max-w-lg w-full p-6 shadow-xl space-y-4 text-[var(--gv-ink)]">
            <div className="flex items-center justify-between pb-3 border-b border-[var(--gv-border)]">
              <h3 className="text-base font-bold text-[var(--gv-ink)]">
                {editingClass ? `Edit Live Class: ${editingClass.id}` : 'Schedule Live Video Class'}
              </h3>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsModalOpen(false)}
              />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3 text-xs">
              <Input
                label="Class Topic / Title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Sutra Navtatva Chapter 4"
              />

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Level Allocation</label>
                  <select
                    value={formData.level}
                    onChange={(e) => setFormData({ ...formData, level: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="All Levels">All Levels (Assembly)</option>
                    <option value="Level 1 - Prarambhik">Level 1 - Prarambhik</option>
                    <option value="Level 2 - Madhyamik">Level 2 - Madhyamik</option>
                    <option value="Level 3 - Shravak Junior">Level 3 - Shravak Junior</option>
                  </select>
                </div>
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Batch Slot</label>
                  <select
                    value={formData.batch}
                    onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Batch A & B Combined">Batch A & B Combined</option>
                    <option value="Batch A - Weekend Morning">Batch A - Weekend Morning</option>
                    <option value="Batch B - Weekend Evening">Batch B - Weekend Evening</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Assigned Instructor</label>
                  <select
                    value={formData.teacherId}
                    onChange={(e) => setFormData({ ...formData, teacherId: e.target.value })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    {teachers.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.title})</option>
                    ))}
                  </select>
                </div>
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Class Category</label>
                  <select
                    value={formData.classType}
                    onChange={(e) => setFormData({ ...formData, classType: e.target.value as 'Regular' | 'General' })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Regular">Regular Class</option>
                    <option value="General">General Assembly</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Date & Time String"
                  type="text"
                  required
                  className="font-mono"
                  value={formData.dateTime}
                  onChange={(e) => setFormData({ ...formData, dateTime: e.target.value })}
                  placeholder="2026-07-26 10:00 AM"
                />
                <div className="w-full space-y-1.5">
                  <label className="block text-xs font-bold text-[var(--gv-ink)]">Session Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as typeof formData.status })}
                    className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3.5 py-2 text-xs text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
                  >
                    <option value="Scheduled">Scheduled</option>
                    <option value="LIVE NOW">LIVE NOW</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              <Input
                label="Zoom / Google Meet URL"
                type="text"
                required
                className="font-mono"
                value={formData.meetingLink}
                onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                placeholder="https://zoom.us/j/..."
              />

              <div className="pt-2 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  Save Class Schedule
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
